<template>
    <div class="columns">
        <div class="column">
            <div class="is-flex is-justify-content-center mb-5">
                <figure class="image przelewy-logo-width">
                    <img src="https://static.martialmatch.com/images/help-center/b701c54b.png" />
                </figure>
            </div>

            <div v-if="configurationFlow === 'create_new'">
                <div class="mt-2 mb-2">
                    <div class="buttons">
                        <a class="button" :href="przelewy24SignUpUrl" target="_blank">
                            <span class="icon">
                                <i class="fas fa-plus"></i>
                            </span>
                            <span>{{ translate("pricing.create_p24_account") }}</span>
                        </a>
                    </div>
                </div>

                <div class="field">
                    <label class="label">{{ translate("pricing.config_name") }}:</label>
                    <div class="control">
                        <input type="text" class="input" v-model="configName" />
                    </div>
                    <p class="help">
                        {{ translate("pricing.config_name_description") }}
                    </p>
                </div>

                <div class="field">
                    <label class="label">{{ translate("pricing.p24_account_id") }}:</label>
                    <div class="control">
                        <input type="text" class="input" v-model="accountId" />
                    </div>
                </div>

                <div class="field">
                    <label class="label">{{ translate("pricing.p24_api_key") }}:</label>
                    <div class="control">
                        <input type="text" class="input" v-model="apiKey" />
                    </div>
                </div>

                <div class="field">
                    <label class="label">{{ translate("pricing.p24_crc_key") }}:</label>
                    <div class="control">
                        <input type="text" class="input" v-model="crcKey" />
                    </div>
                </div>

                <div class="field is-grouped">
                    <div class="control">
                        <LoadingButton
                            :is-loading="creatingNewInProgress"
                            class="button is-primary"
                            @click="createConfiguration"
                            :disabled="!newFormIsValid"
                        >
                            <span class="icon">
                                <i class="fas fa-check"></i>
                            </span>
                            <span>{{ translate("common.save") }}</span>
                        </LoadingButton>
                    </div>

                    <div class="control">
                        <LoadingButton
                            :is-loading="connectionCheckInProgress"
                            class="button"
                            @click="checkConnection"
                            :disabled="!connectionCanBeChecked"
                        >
                            <span class="icon">
                                <i class="fas fa-plug"></i>
                            </span>
                            <span>{{ translate("pricing.check_connection") }}</span>
                        </LoadingButton>
                    </div>
                </div>
            </div>

            <div v-if="configurationFlow === 'use_existing'"></div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { ref, computed } from "vue";
    import LoadingButton from "../../Common/LoadingButton.vue";
    import { translate } from "../../Common/translations";
    import { handleError } from "../../Common/errorHandler";
    import { PROVIDER_PRZELEWY24, Przelewy24ProviderConfiguration } from "../../Api/paymentGatewayConfig";
    import paymentGatewayConfigApi from "../../Api/paymentGatewayConfig";
    import { successToast, errorToast } from "../../Common/toasts";
    import { usePaymentGatewayConfigStore, usePricingSetupStore } from "../pricingSetupApp";

    const store = usePricingSetupStore();
    const paymentGatewayConfigStore = usePaymentGatewayConfigStore();

    const creatingNewInProgress = ref(false);
    const configurationFlow = ref("create_new");

    const configName = ref("");
    const accountId = ref("");
    const apiKey = ref("");
    const crcKey = ref("");

    const connectionCheck = ref(false);
    const connectionCheckInProgress = ref(false);

    const userId = computed(() => store.userId);
    const przelewy24SignUpUrl = computed(() => paymentGatewayConfigStore.przelewy24SignUpUrl);

    const newFormIsValid = computed(() => {
        return (
            configName.value.length > 0 &&
            accountId.value.length > 0 &&
            apiKey.value.length > 0 &&
            crcKey.value.length > 0 &&
            connectionCheck.value
        );
    });

    const connectionCanBeChecked = computed(() => {
        return accountId.value.length > 0 && apiKey.value.length > 0;
    });

    const createConfiguration = () => {
        creatingNewInProgress.value = true;
        return paymentGatewayConfigApi
            .createConfig(userId.value, configName.value, PROVIDER_PRZELEWY24, {
                accountId: accountId.value,
                apiKey: apiKey.value,
                crcKey: crcKey.value,
            } as Przelewy24ProviderConfiguration)
            .then(createdConfig => {
                paymentGatewayConfigStore.chosenConfig = createdConfig;
                paymentGatewayConfigStore.view = "general_configuration";
            })
            .catch(handleError)
            .finally(() => {
                creatingNewInProgress.value = false;
            });
    };

    const checkConnection = () => {
        connectionCheckInProgress.value = true;
        return paymentGatewayConfigApi
            .checkConnection<Przelewy24ProviderConfiguration>(userId.value, {
                provider: PROVIDER_PRZELEWY24,
                providerConfiguration: {
                    accountId: accountId.value,
                    apiKey: apiKey.value,
                    crcKey: crcKey.value,
                },
            })
            .then(res => {
                if (res.success) {
                    connectionCheck.value = true;
                    successToast(translate("pricing.check_connection.success"));
                } else {
                    connectionCheck.value = false;
                    errorToast(translate("pricing.check_connection.fail"));
                }
            })
            .catch(handleError)
            .finally(() => {
                connectionCheckInProgress.value = false;
            });
    };
</script>

<style scoped>
    .przelewy-logo-width {
        width: 128px;
        background-color: whitesmoke;
        padding: 5px;
    }
</style>
